import React, { useEffect, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { GenericSignal, SignalThreadAccessEvent, SignalType } from "@types";
import { useAppDispatch, useAppSelector } from "@store";
import { accessEventsListSelector, getThreadAccessEvents } from "@store/signals";
import { AccessEventItem } from "./components";
import css from "./EmailOpenedSignalContent.module.scss";

export interface EmailOpenedSignalContentProps {
  signal?: GenericSignal<SignalType.EmailOpened>;
  scrollbarRef: React.MutableRefObject<HTMLDivElement>;
}

const EmailOpenedSignalContent: React.FC<EmailOpenedSignalContentProps> = ({
  signal,
  scrollbarRef,
}) => {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector((state) => state.signals.isLoading.threadAccessEvents);
  const { list, headerItemIndices } = useAppSelector(accessEventsListSelector);

  const headerItemIndicesSet = useMemo(() => new Set(headerItemIndices), [headerItemIndices]);

  const rowVirtualizer = useVirtualizer({
    overscan: 0,
    count: isLoading ? list.length + 10 : list.length,
    getScrollElement: () => scrollbarRef.current,
    estimateSize: (index) => (headerItemIndicesSet.has(index) && list.length ? 40 : 80),
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const renderAccessEventsList = () =>
    virtualItems.map(({ index, size, start }) => {
      const item = list[index];
      const isLoaderRow = index > list.length - 1;
      const isHeaderRow = headerItemIndicesSet.has(index);
      const style = {
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: "100%",
        height: `${size}px`,
        transform: `translateY(${start}px)`,
      };

      const attrs = {
        header: {
          style,
          className: css.header,
        },
        loader: {
          style,
          isLoading: true,
        },
        accessEventItem: {
          style,
          event: item as SignalThreadAccessEvent,
        },
      };

      if (isLoaderRow) {
        return <AccessEventItem {...attrs.loader} key={index} />;
      }

      if (isHeaderRow && typeof item === "string") {
        return (
          <div {...attrs.header} key={index}>
            {item}
          </div>
        );
      }

      return <AccessEventItem {...attrs.accessEventItem} key={index} />;
    });

  const attrs = {
    container: {
      className: css.container,
      style: {
        height: `${rowVirtualizer.getTotalSize()}px`,
      },
    },
    header: {
      className: css.header,
    },
  };

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    const hasNextPage = !!signal?.eventMetadata?.accessEvents?.nextPage;

    if (signal && lastItem.index >= list.length - 1 && hasNextPage && !isLoading) {
      dispatch(getThreadAccessEvents({ signal }));
    }
  }, [isLoading, list, signal, virtualItems]);

  return <div {...attrs.container}>{renderAccessEventsList()}</div>;
};

export default EmailOpenedSignalContent;
